/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { PrinterFinderListItem } from '@gh/builder-shared';
import {
  ConstructionButStatusDto,
  ConstructionScheduleFilterDto,
  ConstructionScheduleListItemDto,
  ContactListFilterDto,
  ContactListItemDto,
  ContactVendorListItemDto,
  JobListFilterDto,
  JobListItemDto,
  JobProgressReportConfigDto,
  JobScheduleCreateDto,
  MaintenanceScheduleFilterDto,
  MaintenanceScheduleListItemDto,
  PreConstructionScheduleFilterDto,
  PreConstructionScheduleListItemDto,
  reportField,
  ReportFilterDto,
  ReportFilterFieldDto,
  reportIdField,
  SalesScheduleCreateDto,
  SalesScheduleFilterDto,
  SalesScheduleListItemDto,
  ScheduleSupervisorReportRequestDto,
  ScheduleSupervisorTaskDto,
  ScheduleSupervisorTaskFilterDto,
  ScheduleTemplateListItemDto,
  ScheduleVendorReportRequestDto,
  UserListFilterDto,
  UserListItemDto,
} from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
} from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { emptySelectionModel, SelectionModel, toIsoDate } from '@gh/core-data';
import { addDays, currentNormalizedDate, endOfDay } from '@gh/core-util';
import { intersection, isEmpty, map, omit } from 'lodash';

import {
  ScheduleJobProgressFilter,
  ScheduleJobProgressFilterSupervisorType,
} from './schedule-details.model';

export type ConstructionScheduleListItem = ConstructionScheduleListItemDto;
export type ConstructionScheduleFilter = ConstructionScheduleFilterDto;
export type PreConstructionScheduleListItem = PreConstructionScheduleListItemDto;
export type PreConstructionScheduleFilter = PreConstructionScheduleFilterDto;
export type MaintenanceScheduleListItem = MaintenanceScheduleListItemDto;
export type MaintenanceScheduleFilter = MaintenanceScheduleFilterDto;
export type SalesScheduleListItem = SalesScheduleListItemDto;
export type SalesScheduleFilter = SalesScheduleFilterDto;
export type ScheduleTemplateListItem = ScheduleTemplateListItemDto;
export type JobListItem = JobListItemDto;
export type JobListFilter = JobListFilterDto;
export type SalesFolderListItem = AutocompleteItem;
export type JobScheduleCreate = JobScheduleCreateDto;
export type SalesScheduleCreate = SalesScheduleCreateDto;
export type ScheduleJobProgressUserAutocompleteFilter = UserListFilterDto;
export type ScheduleJobProgressUserAutocomplete = UserListItemDto;
export type JobProgressReportConfig = JobProgressReportConfigDto;
export type ContactVendorListItem = ContactVendorListItemDto;
export type ReportFilterField = ReportFilterFieldDto;
export type ConstructionButStatus = ConstructionButStatusDto;

export enum SchedulesSupervisorReportStep { Task = 1, Supplier = 2, Printer = 3 }

export enum SchedulesSupervisorPrintMode { Both = 'Both', Printed = 'Printed', Emailed = 'Emailed' }

export type ScheduleSupervisorReportFilter = {
  startDate: Date;
  endDate: Date;
  supervisor?: ScheduleSupervisorListItem;
  showUnproduced: boolean;
  showZeroCost: boolean;
  isAllSupervisors: boolean;
  orderBy: string;
  showCriticalPathDescription: boolean;
  isNewPageForStartDate: boolean;
  isNewPageForEachActivity: boolean;
};
export type ScheduleSupervisorReportTask = ScheduleSupervisorTaskDto;
export type ScheduleSupervisorListItem = UserListItemDto;
export type SchedulesSupervisorReportDetails = {
  step: SchedulesSupervisorReportStep;
  taskFilter: FormStateSnapshot<ScheduleSupervisorReportFilter>;
  taskSelectionModel: SelectionModel;
  supplierSelectionModel: SelectionModel;
  printMode: SchedulesSupervisorPrintMode;
  isPrintEmailAttachments: boolean;
  printer?: PrinterFinderListItem;
  printers: PrinterFinderListItem[];
  printerName: string;
  taskExists: boolean;
};
export type ContactListFilter = ContactListFilterDto;
export type ContactListItem = ContactListItemDto;

export const schedulesSupervisorReportDetailsInit = (showUnproduced: boolean): SchedulesSupervisorReportDetails => ({
  step: SchedulesSupervisorReportStep.Task,
  taskFilter: formStateSnapshot({
    startDate: currentNormalizedDate(),
    endDate: addDays(currentNormalizedDate(), 7),
    showUnproduced,
    showZeroCost: false,
    showCriticalPathDescription: false,
    orderBy: 'costCenterCode',
    isAllSupervisors: true,
    isNewPageForStartDate: false,
    isNewPageForEachActivity: false,
    printMode: SchedulesSupervisorPrintMode.Both,
  }),
  taskSelectionModel: emptySelectionModel(),
  supplierSelectionModel: emptySelectionModel(),
  printMode: SchedulesSupervisorPrintMode.Both,
  isPrintEmailAttachments: false,
  printer: void 0,
  printers: [],
  printerName: '',
  taskExists: false,
});

const JOB_PROGRESS_PRINTER_ENTITY_CONFIG = { name: { editable: true } };
export const jobProgressPrinterFieldState = fieldStateByEntityConfig(JOB_PROGRESS_PRINTER_ENTITY_CONFIG);
export const getJobProgressPrinterId = ({ cloudPrinterId }: PrinterFinderListItem) => cloudPrinterId;

export function prepareJobProgressFilter(jobProgressFilter: ScheduleJobProgressFilter,
                                         params: Hash<any>): ScheduleJobProgressFilter {
  return {
    ...jobProgressFilter,
    confirmations: {
      ...jobProgressFilter.confirmations,
      skipSupplierDeliveryMethodCheck: params.skipSupplierDeliveryMethodCheck || false,
    },
  };
}

export function jobProgressReportFilterToDto(jobProgressFilter: ScheduleJobProgressFilter): ReportFilterDto {
  return [
    reportField('WhichSupervisor', jobProgressFilter.supervisorType),
    reportField(
      'Supervisor',
      jobProgressFilter.supervisor ? jobProgressFilter.supervisor.userId : void 0,
      jobProgressFilter.supervisorType === ScheduleJobProgressFilterSupervisorType.Selected),
    reportField('IncludeCompletedJobs', jobProgressFilter.isCompleted),
    reportField('IncludeFinalizedJobs', jobProgressFilter.isFinalized),
    reportField('ShowCriticalPathCC', jobProgressFilter.criticalPathCc),
  ];
}

const SCHEDULES_SUPERVISOR_TASK_FILTER: EntityConfig<ScheduleSupervisorReportFilter> = {
  startDate: {
    required: true,
    type: DateTimeTypes.ShortDate,
    validator: (filter, field, date) =>
      date > filter.endDate ? { startDate: { messageId: 'msg_incorrect_start_date' } } : void 0,
  },
  endDate: {
    required: true,
    type: DateTimeTypes.ShortDate,
    validator: (filter, field, date) =>
      date < filter.startDate ? { endDate: { messageId: 'msg_incorrect_end_date' } } : void 0,
  },
  supervisor: { required: (filter) => !filter.isAllSupervisors },
};

export const validateSchedulesSupervisorTaskFilter = entityValidatorByEntityConfig(SCHEDULES_SUPERVISOR_TASK_FILTER);

export const isScheduleSupervisorFieldsAffectFilter = (differentFields: string[]) => {
  const unaffectFields = ['showCriticalPathDescription', 'isNewPageForStartDate', 'isNewPageForEachActivity'];
  return intersection(differentFields, unaffectFields).length !== differentFields.length;
};

export const scheduleSupervisorReportFilterToDto = (filter: ScheduleSupervisorReportFilter,
                                                    printMode: SchedulesSupervisorPrintMode = SchedulesSupervisorPrintMode.Both): ScheduleSupervisorTaskFilterDto => ({
  startDate: filter.startDate,
  endDate: endOfDay(filter.endDate),
  supervisorId: filter.isAllSupervisors ? void 0 : filter.supervisor && filter.supervisor.userId,
  excludeUnproduced: !filter.showUnproduced,
  excludeZeroCost: !filter.showZeroCost,
  isPrinted: {
    [SchedulesSupervisorPrintMode.Emailed]: false,
    [SchedulesSupervisorPrintMode.Printed]: true,
    [SchedulesSupervisorPrintMode.Both]: void 0,
  }[printMode],
});

export type SupervsiorReportRequestInput = {
  details: SchedulesSupervisorReportDetails;
  skipDeliveryCheck: boolean;
  authCode?: string;
  printerId?: ID;
};

export const createSupervisorReportRequestDto = (input: SupervsiorReportRequestInput): ScheduleSupervisorReportRequestDto => {
  const {
    details,
    skipDeliveryCheck,
    authCode,
    printerId,
  } = input;
  const taskFilter = getFormStateSnapshotValue(details.taskFilter);
  return {
    ...scheduleSupervisorReportFilterToDto(taskFilter, details.printMode),
    allSupervisors: taskFilter.isAllSupervisors,
    authCode,
    printerId,
    tasks: details.supplierSelectionModel,
    showCpDescription: taskFilter.showCriticalPathDescription,
    orderBy: { 'startDate': 'StartDate', 'costCenterCode': 'CostCenter' }[taskFilter.orderBy],
    confirmations: {
      skipSupplierDeliveryMethodCheck: skipDeliveryCheck,
    },
  };
};

export type ScheduleVendorFilterDialogConfig = Record<string, never>;
export type ScheduleConstructionReportDialogConfig = Record<string, never>;
export type ScheduleConstructionReportDialogRequest = Record<string, never>;
export type ScheduleVendorReportRequest = ScheduleVendorReportRequestDto & {
  worksheetFormat: boolean;
  sendSuppliers: boolean;
  sendSupervisors: boolean;
  showCompletedTasks: boolean;
  vendorList: ContactVendorListItem[];
  salespersonSelectType: string;
};

export const vendorReportRequestToDto =
  (vendorReport: ScheduleVendorReportRequest): ScheduleVendorReportRequestDto => ({
    ...<ScheduleVendorReportRequestDto>omit(vendorReport, [
      'worksheetFormat',
      'vendorList',
      'salespersonSelectType',
    ]),
    vendorIds: isEmpty(vendorReport.vendorList) ?
      void 0 : map(vendorReport.vendorList, ({ contactId }: ContactVendorListItem) => contactId),
    allVendors: isSalespersonSelectTypeAllVendors(vendorReport.salespersonSelectType),
  });

export function vendorReportRequestToReportRequest(vendorReportRequest: ScheduleVendorReportRequest): ReportFilterDto {
  return [
    ...composeReportVendorFields(vendorReportRequest.vendorList),
    reportField('AllVendors', isSalespersonSelectTypeAllVendors(vendorReportRequest.salespersonSelectType)),
    reportField('From', toIsoDate(vendorReportRequest.from)),
    reportField('To', toIsoDate(vendorReportRequest.to)),
    reportField('Worksheetformat', vendorReportRequest.worksheetFormat),
    reportField('FromBuilderPortal', true),
    reportField('ShowCompletedTasks', vendorReportRequest.showCompletedTasks),
  ];
}

export function composeReportVendorFields(vendorList: ContactVendorListItem[]): ReportFilterField[] {
  return map(vendorList, ({ contactId }: ContactVendorListItem) => reportIdField('Vendor', contactId));
}

export const isSalespersonSelectTypeAllVendors = (type: string) =>
  type === SalespersonSelectType.All;

export const isSupervisorSelectTypeSelect = (type: string) =>
  type === SupervisorSelectType.Select;

export enum SalespersonSelectType {
  All = 'ALL_VENDORS',
  Select = 'SELECTED_VENDORS',
}

export enum SupervisorSelectType {
  All = 'ALL',
  Select = 'SELECTED',
  Unassigned = 'UNASSIGNED',
}
